import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	SovosButton,
	SovosDialog,
	SovosDialogActions,
	SovosDialogContent,
	SovosDialogTitle,
	SovosPageContentGeneric,
	SovosPageLayout,
	SovosStep,
	SovosStepButton,
	SovosStepper,
} from 'mosaic-react';
import React from 'react';
import { useCallback, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { fetchConfiguration } from 'src/api/configuration';
import type { fetchRateFiles } from 'src/api/rateFile';
import { createRateFile } from 'src/api/rateFile';
import {
	XSTORE_FORMAT_MULTI_FILES,
	XSTORE_FORMAT_SINGLE_FILE,
} from 'src/helpers/constant';
import { REP_EXTENSION } from 'src/helpers/constant';
import useBlocker from 'src/hooks/useBlocker';
import type { NewRateFileInput, RateFile } from 'src/interfaces/RateFile';

import type { NewRateFileFormState } from './NewRateFileFormTypes';
import StepFileDetails from './formSteps/StepFileDetails';
import StepFileFormat from './formSteps/StepFileFormat';
import StepGoodServiceCodes from './formSteps/StepGoodServiceCodes';
import StepLocations from './formSteps/StepLocations';
import StepReviewAndCreate from './formSteps/StepReviewAndCreate';

const stepsOrder = [
	'File details',
	'Locations',
	'Good/Service codes',
	'Rate file format',
	'Review and create',
] as const;

const importFromGTD = 'Import from GTD' as const;

const NewRateFilePage: React.FC = () => {
	const [showError, setShowError] = useState(false);

	const queryClient = useQueryClient();

	const navigate = useNavigate();

	const configurationQuery = useQuery(['configuration'], fetchConfiguration, {
		onSuccess: (data) => {
			setFormState({
				...formState,
				settings: {
					...formState.settings,
					gtdEntityUseCode: data.xstoreSettings.gtdEntityUseCode,
					goodServiceCodeMapping: data.xstoreSettings.goodServiceCodeMapping,
					useProductCode: data.xstoreSettings.useProductCode,
					extension: data.xstoreSettings.extension,
				},
			});
		},
	});

	const createNewRateFileMutation = useMutation(createRateFile, {
		onMutate: () => {
			setShowError(false);
		},

		onSuccess: (data) => {
			queryClient.setQueriesData(
				{ queryKey: ['rateFiles'] },
				(cachedData?: Awaited<ReturnType<typeof fetchRateFiles>>) => {
					if (cachedData) {
						const createdFile: RateFile = {
							...data,
							status: data.status || 'Processing',
							outputFormatName: '',
						};

						return {
							...cachedData,
							items: [createdFile, ...cachedData.items],
						};
					}

					return cachedData;
				}
			);
		},

		onError: () => {
			setShowError(true);
		},
	});

	const [currentStep, setCurrentStep] = useState(0);

	const [formState, setFormState] = useState<NewRateFileFormState>({
		locations: {
			type: 'import',
			value: importFromGTD,
		},
		settings: {
			gtdGsc: true,
			xstoreMultiFile: false,
			gtdEntityUseCode:
				configurationQuery.data?.xstoreSettings.gtdEntityUseCode || false,
			goodServiceCodeMapping:
				configurationQuery.data?.xstoreSettings.goodServiceCodeMapping || false,
			useProductCode:
				configurationQuery.data?.xstoreSettings.useProductCode || true,
			extension:
				configurationQuery.data?.xstoreSettings.extension || REP_EXTENSION,
		},
	});

	const [exitConfirmationDialog, setExitConfirmationDialog] = useState<{
		isOpen: boolean;
		continue: (() => void) | null;
	}>({
		isOpen: false,
		continue: null,
	});

	useBlocker(
		(tx) => setExitConfirmationDialog({ isOpen: true, continue: tx.retry }),
		Object.keys(formState).length > 0 &&
			!exitConfirmationDialog.isOpen &&
			!createNewRateFileMutation.isSuccess
	);

	const checkReviewAndCreateError = () => {
		setShowError(showError && false);
	};

	const handleOnContinue = useCallback(() => {
		setCurrentStep(currentStep + 1);
	}, [currentStep]);

	const getLocationValueToShowInReviewAndCreateStep = (): any => {
		if (formState.locations?.type === 'select') {
			return formState.locations.value
				?.map((location) => location.name)
				.sort()
				.join(', ');
		}

		if (formState.locations?.type === 'upload') {
			return `${formState.locations?.addressFileName} - ${formState.locations?.locationMapping?.name}`;
		}

		return importFromGTD;
	};

	const getRateFileFormatValueToShowInReviewAndCreateStep = (): string => {
		if (formState.outputFormat?.name.includes('xStore') && formState.settings) {
			return formState.settings.xstoreMultiFile
				? XSTORE_FORMAT_MULTI_FILES
				: XSTORE_FORMAT_SINGLE_FILE;
		}

		return (formState.outputFormat && formState.outputFormat.name) || '';
	};

	const getLocationValueToSetFormState = (
		locations: NewRateFileFormState['locations']
	): any => {
		if (formState.locations && formState.locations.type === locations?.type) {
			return { ...formState.locations, ...locations };
		}

		if (locations?.type === 'import') {
			return { type: locations.type, value: importFromGTD };
		}

		return locations;
	};

	const steps = {
		'File details': (
			<StepFileDetails
				fileName={formState.name}
				setFileName={(name) => setFormState({ ...formState, name })}
				inputDate={formState.inputDate}
				setInputDate={(inputDate) => setFormState({ ...formState, inputDate })}
				onContinue={handleOnContinue}
			/>
		),
		Locations: (
			<StepLocations
				selectedLocations={formState.locations}
				setSelectedLocations={(locations) =>
					setFormState((formState) => ({
						...formState,
						locations: getLocationValueToSetFormState(locations),
					}))
				}
				onContinue={handleOnContinue}
				stateLimit={configurationQuery.data?.stateLimit || null}
			/>
		),
		'Good/Service codes': (
			<StepGoodServiceCodes
				selectedCodes={formState.rateFileGscList}
				setSelectedCodes={(rateFileGscList) =>
					setFormState((formState) => ({ ...formState, rateFileGscList }))
				}
				selectedFileName={formState.rateFileGscListFileName}
				setSelectedFileName={(rateFileGscListFileName) =>
					setFormState((formState) => ({
						...formState,
						rateFileGscListFileName,
					}))
				}
				onContinue={handleOnContinue}
				gscLimit={configurationQuery.data?.gscLimit || null}
				selectedSettings={formState.settings}
				setSelectedSettings={(formStateSettings) =>
					setFormState({
						...formState,
						settings: {
							...formState.settings,
							gtdGsc: formStateSettings && formStateSettings.gtdGsc,
						},
					})
				}
			/>
		),
		'Rate file format': (
			<StepFileFormat
				selectedFormat={formState.outputFormat}
				setSelectedFormat={(outputFormat) =>
					setFormState({ ...formState, outputFormat })
				}
				selectedSettings={formState.settings}
				setSelectedSettings={(formStateSettings) =>
					setFormState({
						...formState,
						settings: {
							...formState.settings,
							xstoreMultiFile: formStateSettings?.xstoreMultiFile ?? false,
						},
					})
				}
				onContinue={() => {
					handleOnContinue();
					checkReviewAndCreateError();
				}}
				outputFormats={configurationQuery.data?.outputTypes || []}
				outputFormatsIsLoading={configurationQuery.isLoading}
			/>
		),
		'Review and create': (
			<StepReviewAndCreate
				setCurrentStep={setCurrentStep}
				steps={[
					{ name: 'File details', value: `File name: ${formState.name}` },
					{
						name: 'Locations',
						value: getLocationValueToShowInReviewAndCreateStep(),
					},
					{
						name: 'Good/Service codes',
						value: formState.settings?.gtdGsc
							? 'Import from GTD'
							: formState.rateFileGscListFileName,
					},
					{
						name: 'Rate file format',
						value: getRateFileFormatValueToShowInReviewAndCreateStep(),
					},
				]}
				onCreate={() => {
					const { rateFileGscListFileName, ...newRateFile } = formState;
					return createNewRateFileMutation.mutate(
						newRateFile as NewRateFileInput
					);
				}}
				isSubmitting={createNewRateFileMutation.isLoading}
				showError={showError}
			/>
		),
	};

	return createNewRateFileMutation.isSuccess ? (
		<Navigate
			to='../'
			state={{ savedRateFileId: createNewRateFileMutation.data.id }}
		/>
	) : (
		<SovosPageLayout
			pageTitle='Create New Rate File'
			pageCrumbs={[{ text: 'Rate Files', onClick: () => navigate('../') }]}
		>
			<SovosPageContentGeneric
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					maxHeight: '100%',
				}}
			>
				<SovosStepper
					activeStep={currentStep}
					sx={{ px: 0, pt: 0, ml: -1, alignSelf: 'stretch' }}
				>
					{stepsOrder.map((step, i) => (
						<SovosStep key={step}>
							<SovosStepButton onClick={() => setCurrentStep(i)}>
								{step}
							</SovosStepButton>
						</SovosStep>
					))}
				</SovosStepper>
				{steps[stepsOrder[currentStep]]}
			</SovosPageContentGeneric>

			<SovosDialog open={exitConfirmationDialog.isOpen}>
				<SovosDialogTitle>Cancel create new rate file?</SovosDialogTitle>
				<SovosDialogContent>
					The rate file information you’ve entered will be lost. Are you sure
					you want to cancel?
				</SovosDialogContent>
				<SovosDialogActions>
					<SovosButton
						variant='text'
						onClick={() =>
							setExitConfirmationDialog({
								isOpen: false,
								continue: null,
							})
						}
					>
						Keep editing
					</SovosButton>
					<SovosButton
						onClick={() => {
							if (exitConfirmationDialog.continue)
								exitConfirmationDialog.continue();
						}}
						color='error'
					>
						Cancel rate file
					</SovosButton>
				</SovosDialogActions>
			</SovosDialog>
		</SovosPageLayout>
	);
};

export default NewRateFilePage;
